// Blocs du tableau de bord Performance (proposition visuelle)
import { useMemo } from "react";
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus, Trophy, WarningOctagon } from "@phosphor-icons/react";

export const fmt = (v, unit) => {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Number(v);
  const s = n.toLocaleString("fr-FR", { maximumFractionDigits: unit === "percent" ? 1 : 0 });
  if (unit === "currency") return `${s} €`;
  if (unit === "percent") return `${s} %`;
  if (unit === "duration") return `${s} min`;
  return s;
};

// Objectif ramené au mois complet (21 jours ouvrés de référence)
export const monthlyTarget = (m) => {
  const t = m.target_raw;
  if (!t) return null;
  if (m.unit === "percent" || m.snapshot) return t.value;
  if (t.basis === "month") return t.value;
  if (t.basis === "week") return t.value * 4.2;
  return t.value * 21;
};

// Seuils réglables depuis Administration Performance (par défaut vert ≥ 90 %, orange 50 – 90 %, rouge < 50 %)
export const THRESHOLDS = { green_min: 90, amber_min: 50, down_amber_max: 150 };
export const applyThresholds = (t) => Object.assign(THRESHOLDS, t || {});

export const statusOf = (value, target, direction) => {
  if (value == null || target == null || target === 0) return null;
  const ratio = (value / target) * 100;
  if (direction === "down") {
    if (ratio <= 100) return "green";
    return ratio <= THRESHOLDS.down_amber_max ? "amber" : "rose";
  }
  if (ratio >= THRESHOLDS.green_min) return "green";
  return ratio >= THRESHOLDS.amber_min ? "amber" : "rose";
};

export const STATUS_STYLE = {
  green: { row: "bg-emerald-50/50", bar: "border-l-emerald-600", text: "text-emerald-800", dot: "bg-emerald-600", fill: "bg-emerald-600", label: "Objectif tenu" },
  amber: { row: "bg-amber-50/50", bar: "border-l-amber-500", text: "text-amber-800", dot: "bg-amber-500", fill: "bg-amber-500", label: "À surveiller" },
  rose: { row: "bg-rose-50/50", bar: "border-l-rose-600", text: "text-rose-800", dot: "bg-rose-600", fill: "bg-rose-600", label: "En alerte" },
  none: { row: "", bar: "border-l-zinc-200", text: "text-zinc-400", dot: "bg-zinc-300", fill: "bg-zinc-300", label: "Pas de donnée" },
};

export const Delta = ({ value, direction, suffix = "vs mois dernier" }) => {
  if (value == null) return <span className="text-zinc-400 text-xs">—</span>;
  const good = direction === "down" ? value < 0 : value > 0;
  const Icon = value === 0 ? Minus : value > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium tabular-nums ${value === 0 ? "text-zinc-500" : good ? "text-emerald-700" : "text-rose-700"}`}>
      <Icon size={13} weight="bold" />
      {value > 0 ? "+" : ""}{value} %
      {suffix && <span className="text-zinc-400 font-normal">{suffix}</span>}
    </span>
  );
};

// ---------- Bloc : avancement de la période ----------
const PERIOD_LABEL = { day: "du jour", week: "de la semaine", month: "du mois" };

export function BlockAvancement({ metrics, workdays, period }) {
  const rows = useMemo(
    () => metrics.filter((m) => m.target != null && !m.is_threshold && m.direction !== "down").slice(0, 6),
    [metrics]
  );
  if (rows.length === 0) return <Empty text="Aucun objectif défini sur cette période." />;
  const pace = Math.round((workdays.done / Math.max(workdays.total, 1)) * 100);
  return (
    <div className="divide-y divide-zinc-100" data-testid="perf-block-avancement">
      {rows.map((m) => {
        const target = m.target;
        const pct = m.value == null ? null : Math.round((m.value / target) * 100);
        const st = STATUS_STYLE[statusOf(m.value, m.expected_to_date, m.direction) || "none"];
        const reste = m.value == null ? null : Math.max(0, Math.round(target - m.value));
        const rythme = reste == null || workdays.left <= 0 ? null : Math.ceil(reste / workdays.left);
        return (
          <div key={m.metric_id} className="py-3 first:pt-0 last:pb-0" data-testid={`perf-avancement-${m.metric_id}`}>
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{m.name}</div>
                <div className="label-tiny text-zinc-400">{m.service_name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading text-lg font-semibold tabular-nums">
                  {fmt(m.value, m.unit)} <span className="text-zinc-300 font-normal">/ {fmt(target, m.unit)}</span>
                </div>
                <div className={`text-xs font-semibold tabular-nums ${st.text}`}>
                  {pct == null ? "—" : `${pct} % de l'objectif ${PERIOD_LABEL[period] || "de la période"}`}
                  <span className="text-zinc-400 font-normal"> · attendu à ce jour {fmt(m.expected_to_date, m.unit)} ({pace} %)</span>
                </div>
              </div>
            </div>
            <div className="mt-2 h-2 bg-zinc-100 relative">
              <div className={`h-full ${st.fill}`} style={{ width: `${Math.min(pct ?? 0, 100)}%` }} />
              <div
                className="absolute top-0 h-full w-px bg-zinc-900"
                style={{ left: `${Math.min(pace, 100)}%` }}
                title="Attendu à ce jour"
              />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[11px] text-zinc-500">
              <span>{workdays.left > 0 ? `Reste ${reste == null ? "—" : fmt(reste, m.unit)} sur ${workdays.left} jour(s) ouvré(s)` : "Période terminée"}</span>
              <span className="tabular-nums">
                {m.previous == null ? `aucune donnée sur la période précédente` : `${fmt(m.previous, m.unit)} sur la période précédente`}
                {rythme != null && ` · soit ${fmt(rythme, m.unit)} / jour`}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Bloc : tableau coloré ----------
const VALUE_HEADER = { day: "Aujourd'hui", week: "Semaine en cours", month: "Mois en cours" };
const TARGET_HEADER = { day: "Objectif du jour", week: "Objectif semaine", month: "Objectif du mois" };

export function BlockTableau({ metrics, summary }) {
  if (metrics.length === 0) return <Empty text="Aucun indicateur actif." />;
  const period = summary?.period || "month";
  const prevLabel = summary?.previous_label || "période précédente";
  const prevEmpty = summary?.previous_days_with_entries === 0;
  return (
    <div className="overflow-x-auto" data-testid="perf-block-tableau">
      <table className="w-full text-sm min-w-[980px]">
        <thead>
          <tr className="border-b border-zinc-200">
            <Th>Service</Th><Th>Indicateur</Th><Th right>{VALUE_HEADER[period]}</Th>
            <Th right>{TARGET_HEADER[period]}</Th><Th right>Attendu à ce jour</Th>
            <Th>Avancement</Th><Th right>{prevLabel}</Th><Th right>Évolution</Th><Th>État</Th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m, i) => {
            const st = STATUS_STYLE[statusOf(m.value, m.expected_to_date, m.direction) || "none"];
            const pct = m.target && m.value != null ? Math.round((m.value / m.target) * 100) : null;
            const sameService = i > 0 && metrics[i - 1].service_id === m.service_id;
            return (
              <tr key={m.metric_id} className={`border-b border-zinc-100 last:border-0 border-l-4 ${st.bar} ${st.row}`} data-testid={`perf-table-row-${m.metric_id}`}>
                <td className="px-3 py-2.5 font-medium whitespace-nowrap">{sameService ? "" : m.service_name}</td>
                <td className="px-3 py-2.5">
                  <span className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                    <span className="truncate">{m.name}</span>
                    {m.direction === "down" && <span className="text-[9px] uppercase tracking-wide text-zinc-400 border border-zinc-200 px-1">bas = mieux</span>}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{fmt(m.value, m.unit)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-zinc-500">{fmt(m.target, m.unit)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-zinc-500">{fmt(m.expected_to_date, m.unit)}</td>
                <td className="px-3 py-2.5 w-[150px]">
                  {pct == null ? <span className="text-zinc-400">—</span> : (
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 bg-white/70 border border-zinc-200 flex-1">
                        <div className={`h-full ${st.fill}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                      <span className={`tabular-nums text-xs font-semibold ${st.text}`}>{pct} %</span>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-zinc-600">
                  {m.previous == null
                    ? <span className="text-amber-700 text-[11px]">{prevEmpty ? "aucune donnée" : "—"}</span>
                    : fmt(m.previous, m.unit)}
                </td>
                <td className="px-3 py-2.5 text-right"><Delta value={m.delta_pct} direction={m.direction} suffix="" /></td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide ${st.text}`}>{st.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Bloc : podium du trio de tête ----------
const MEDAL = [
  { ring: "border-amber-400", badge: "bg-amber-400 text-amber-950", height: "h-24" },
  { ring: "border-zinc-300", badge: "bg-zinc-300 text-zinc-800", height: "h-16" },
  { ring: "border-orange-300", badge: "bg-orange-300 text-orange-950", height: "h-12" },
];

export function BlockPodium({ ranking, periodLabel, previousLabel, complete }) {
  const top = (ranking || []).slice(0, 3);
  if (top.length === 0) return <Empty text="Aucun résultat saisi par collaborateur sur cette période." />;
  const order = [1, 0, 2].filter((i) => top[i]);
  return (
    <div data-testid="perf-block-podium">
      <div className="label-tiny text-zinc-400 mb-3">
        Trio de tête — <span className="text-zinc-600">{periodLabel || "période en cours"}</span>
      </div>
      <div className="flex items-end justify-center gap-2 sm:gap-3">
        {order.map((i) => {
          const r = top[i];
          const med = MEDAL[i];
          const move = r.previous_rank == null ? null : r.previous_rank - r.rank;
          return (
            <div key={r.collaborator_id} className="flex-1 min-w-0 text-center" data-testid={`perf-podium-${r.collaborator_id}`}>
              <div className={`mx-auto w-9 h-9 rounded-full border-2 ${med.ring} flex items-center justify-center bg-white`}>
                {i === 0 ? <Trophy size={16} weight="fill" className="text-amber-500" /> : <span className="text-sm font-semibold text-zinc-600">{i + 1}</span>}
              </div>
              <div className="mt-2 text-sm font-medium truncate" title={r.name}>{r.name}</div>
              <div className="label-tiny text-zinc-400 truncate">{r.service_name}</div>
              <div className="font-heading text-xl font-semibold tabular-nums mt-1">{fmt(r.total, "number")}</div>
              <div className="text-[11px] tabular-nums mt-0.5">
                {move == null ? (
                  <span className="text-zinc-400">nouveau au classement</span>
                ) : move === 0 ? (
                  <span className="text-zinc-500">garde sa {r.rank === 1 ? "1re" : `${r.rank}e`} place</span>
                ) : (
                  <span className={move > 0 ? "text-emerald-700" : "text-rose-700"}>
                    {move > 0 ? "▲" : "▼"} {Math.abs(move)} place{Math.abs(move) > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <div className={`mt-2 ${med.height} ${med.badge} flex items-end justify-center text-[10px] font-semibold uppercase tracking-wide pb-1`}>
                {i + 1}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-100 text-[11px] text-zinc-500 space-y-1">
        <div className="label-tiny text-zinc-400">Écart avec {previousLabel || "la période précédente"}</div>
        {top.map((r, i) => (
          <div key={r.collaborator_id} className="flex items-center justify-between gap-2">
            <span className="truncate">{i + 1}. {r.name}</span>
            <span className="tabular-nums shrink-0">
              {r.previous == null ? (
                <span className="text-amber-700">aucune donnée</span>
              ) : complete ? (
                <>
                  {fmt(r.previous, "number")}
                  <span className={`ml-1.5 font-medium ${r.delta_pct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {r.delta_pct > 0 ? "+" : ""}{r.delta_pct} %
                  </span>
                </>
              ) : (
                <>
                  {fmt(r.previous, "number")}
                  <span className="ml-1.5 text-zinc-600">soit {r.share_pct} % déjà réalisés</span>
                </>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Bloc : champions par service ----------
export function BlockChampions({ services, previousLabel, complete }) {
  if (!services || services.length === 0) return <Empty text="Aucun résultat par collaborateur sur cette période." />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="perf-block-champions">
      {services.map((s) => {
        const champ = s.top[0];
        const move = champ.previous_rank == null ? null : champ.previous_rank - champ.rank;
        return (
          <div key={s.id} className="border border-zinc-200 bg-white" data-testid={`perf-champion-${s.id}`}>
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-zinc-100 bg-zinc-50/60">
              <span className="text-xs font-medium truncate">{s.name}</span>
              <span className="label-tiny text-zinc-400 shrink-0">{s.top.length} suivi(s)</span>
            </div>
            <div className="px-3 py-3">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 shrink-0 bg-amber-50 border border-amber-300 flex items-center justify-center">
                  <Trophy size={15} weight="fill" className="text-amber-500" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate" title={champ.name}>{champ.name}</div>
                  <div className="text-[11px] text-zinc-500">
                    {move == null ? "nouveau champion" : move > 0 ? `▲ ${move} place${move > 1 ? "s" : ""}` : move === 0 ? "conserve son titre" : `▼ ${Math.abs(move)} place${Math.abs(move) > 1 ? "s" : ""}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading text-lg font-semibold tabular-nums leading-none">{fmt(champ.total, "number")}</div>
                  <div className="text-[11px] tabular-nums mt-0.5">
                    {champ.previous == null ? (
                      <span className="text-zinc-400">aucun historique</span>
                    ) : complete ? (
                      <span className={champ.delta_pct >= 0 ? "text-emerald-700" : "text-rose-700"}>
                        {champ.delta_pct > 0 ? "+" : ""}{champ.delta_pct} %
                      </span>
                    ) : (
                      <span className="text-zinc-500">{champ.share_pct} % du précédent</span>
                    )}
                  </div>
                </div>
              </div>
              {s.top.length > 1 && (
                <div className="mt-2.5 pt-2 border-t border-zinc-100 space-y-1">
                  {s.top.slice(1).map((r) => (
                    <div key={r.collaborator_id} className="flex items-center justify-between gap-2 text-[11px] text-zinc-500">
                      <span className="truncate">{r.rank}. {r.name}</span>
                      <span className="tabular-nums shrink-0">{fmt(r.total, "number")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div className="sm:col-span-2 text-[11px] text-zinc-400">
        Mouvements de place calculés par rapport à {previousLabel || "la période précédente"}.
      </div>
    </div>
  );
}

// ---------- Bloc : classement collaborateurs ----------
export function BlockCollaborateurs({ ranking }) {
  if (!ranking || ranking.length === 0) return <Empty text="Ajoutez des collaborateurs dans l'administration pour voir le classement." />;
  const max = Math.max(...ranking.map((r) => r.total));
  return (
    <ol className="space-y-2.5" data-testid="perf-block-classement">
      {ranking.map((r, i) => (
        <li key={r.collaborator_id} className="flex items-center gap-3" data-testid={`perf-rank-${r.collaborator_id}`}>
          <span className={`w-6 h-6 flex items-center justify-center text-xs font-semibold shrink-0 ${i === 0 ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>
            {i === 0 ? <Trophy size={13} weight="fill" /> : i + 1}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium truncate">{r.name}</span>
              <span className="text-sm tabular-nums font-semibold">{fmt(r.total, "number")}</span>
            </div>
            <div className="h-1.5 bg-zinc-100 mt-1">
              <div className="h-full bg-zinc-900" style={{ width: `${max ? (r.total / max) * 100 : 0}%` }} />
            </div>
            <div className="label-tiny text-zinc-400 mt-1">{r.service_name}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---------- Bloc : vigilance (indicateurs "bas = mieux") ----------
export function BlockVigilance({ metrics }) {
  const rows = metrics.filter((m) => m.direction === "down");
  if (rows.length === 0) return <Empty text="Aucun indicateur de vigilance." />;
  return (
    <div className="space-y-2" data-testid="perf-block-vigilance">
      {rows.map((m) => {
        const expected = m.expected_to_date;
        const status = statusOf(m.value, expected, "down") || "none";
        const st = STATUS_STYLE[status];
        return (
          <div key={m.metric_id} className={`border-l-4 ${st.bar} ${st.row} px-3 py-2.5`} data-testid={`perf-vigilance-${m.metric_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  {status === "rose" && <WarningOctagon size={14} weight="fill" className="text-rose-600 shrink-0" />}
                  {m.name}
                </div>
                <div className="label-tiny text-zinc-400">{m.service_name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading text-base font-semibold tabular-nums">{fmt(m.value, m.unit)}</div>
                <div className="text-[11px] text-zinc-500">seuil {fmt(expected, m.unit)}</div>
              </div>
            </div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <Delta value={m.delta_pct} direction="down" suffix="vs période précédente" />
              <span className="text-[11px] text-zinc-400 tabular-nums">
                {m.previous == null ? "aucune donnée avant" : `avant : ${fmt(m.previous, m.unit)}`}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Bloc : graphique 14 jours ----------
export function BlockGraphique({ points, metricId, meta, dailyTarget }) {
  return (
    <div className="h-[250px]" data-testid="perf-block-graphique">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={points || []} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
          <defs>
            <linearGradient id="perfArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#24593C" stopOpacity={0.22} />
              <stop offset="100%" stopColor="#24593C" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#f1f1f3" vertical={false} />
          <XAxis dataKey="date" tickFormatter={(d) => `${d.slice(8)}/${d.slice(5, 7)}`} tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{ borderRadius: 0, border: "1px solid #e4e4e7", fontSize: 12 }}
            labelFormatter={(d) => d.split("-").reverse().join("/")}
            formatter={(v) => [fmt(v, meta?.unit), meta?.name || "Valeur"]}
          />
          {dailyTarget != null && <ReferenceLine y={dailyTarget} stroke="#18181b" strokeDasharray="4 4" />}
          <Area type="monotone" dataKey={metricId} stroke="none" fill="url(#perfArea)" connectNulls />
          <Line type="monotone" dataKey={metricId} stroke="#24593C" strokeWidth={2} dot={{ r: 3 }} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export const Empty = ({ text }) => (
  <div className="border border-dashed border-zinc-300 px-4 py-8 text-center text-sm text-zinc-500">{text}</div>
);

const Th = ({ children, right }) => (
  <th className={`label-tiny text-zinc-400 font-medium px-3 py-2.5 ${right ? "text-right" : "text-left"}`}>{children}</th>
);
